import { useCallback, useState, useRef, useEffect } from "react";
import { getSupabase } from "@/lib/hooks/api/utils";
import type { EntitySearchMode } from "@/lib/search/embedded-search-entities";

export interface FTSSearchResultWithFullData {
  id: string;
  name: string;
  email: string;
  company_name?: string;
  phone?: string;
  title?: string;
  status?: string;
  tags?: string[];
  _relevance?: number; // Relevance score from FTS
  _isPartial?: boolean; // True if still loading full data
  _isLoading?: boolean; // True if row is currently being enhanced
  [key: string]: any; // Full contact object
}

export interface UseFTSSearchWithFullRowsOptions {
  entityType:
    | "contacts"
    | "companies"
    | "deals"
    | "tickets"
    | "tasks"
    | "communications"
    | "offerings"
    | "recurring-transactions"
    | "recurring-charges"
    | "msp-docs"
    | "admin_users"
    | "social_media_posts";
  limit?: number;
  offset?: number;
  debounceMs?: number;
  cacheTtl?: number; // Cache TTL in seconds (default: 1 hour)
  /**
   * FTS route mode. Defaults to "keyword" so every existing caller (pickers, selects,
   * calendar) is unchanged. Table-search surfaces pass "hybrid" for embedded entities
   * (see `searchModeForEntity`) to enable server-side semantic + FTS ranking.
   */
  searchMode?: EntitySearchMode;
}

export interface UseFTSSearchWithFullRowsReturn {
  search: (query: string) => void;
  isLoading: boolean;
  error: string | null;
  results: FTSSearchResultWithFullData[];
  totalCount: number;
  cacheStats?: {
    cached: number;
    fetched: number;
  };
}

/**
 * Hook for performing FTS search and fetching full row data
 * 
 * Implements the industry-standard two-step pattern:
 * 1. FTS search returns IDs + relevance + bare minimum fields (fast, indexed)
 * 2. Fetch full rows by ID with caching (preserves relevance order)
 * 
 * Usage:
 * const { search, isLoading, results, totalCount } = useFTSSearchWithFullRows({
 *   entityType: 'contacts',
 *   limit: 1000,
 *   cacheTtl: 3600, // 1 hour
 * });
 * 
 * search('john smith');
 */
export function useFTSSearchWithFullRows({
  entityType,
  limit = 1000,
  offset = 0,
  debounceMs = 300,
  cacheTtl = 3600,
  searchMode = "keyword",
}: UseFTSSearchWithFullRowsOptions): UseFTSSearchWithFullRowsReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<FTSSearchResultWithFullData[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [cacheStats, setCacheStats] = useState<{ cached: number; fetched: number } | undefined>();

  // Refs for debouncing
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  // Identifies the newest run. Every state write is gated on it, so a slow
  // older search can never overwrite (or blank) a newer one's results — the
  // abort signal alone is not enough, because a response that has already been
  // received still resolves after the abort.
  const runIdRef = useRef(0);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const performSearch = useCallback(
    async (query: string): Promise<void> => {
      const runId = ++runIdRef.current;
      const isCurrent = () => runIdRef.current === runId;

      if (!query || query.trim().length === 0) {
        setResults([]);
        setTotalCount(0);
        setError(null);
        setCacheStats(undefined);
        return;
      }

      // Cancel the previous request before this run starts issuing its own, and
      // hold the controller in a LOCAL so step 2 cannot pick up a newer run's
      // controller (which would make this run uncancellable and would let the
      // next keystroke abort the wrong request).
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const controller = new AbortController();
      abortControllerRef.current = controller;

      setIsLoading(true);
      setError(null);

      try {
        // Get auth token from Supabase session
        const supabase = getSupabase();
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (!session?.access_token) {
          throw new Error("Not authenticated");
        }

        // Step 1: Perform FTS search to get IDs + relevance. Embedded entities pass
        // `?mode=hybrid` so the route embeds the query + runs the `_hybrid` RPC; keyword
        // (the default) omits the param and behaves exactly as before.
        const searchPath =
          searchMode === "hybrid"
            ? `/api/search/${entityType}?mode=hybrid`
            : `/api/search/${entityType}`;
        const searchResponse = await fetch(searchPath, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            query: query.trim(),
            limit,
            offset,
          }),
          signal: controller.signal,
        });

        if (!searchResponse.ok) {
          throw new Error(`Search failed: ${searchResponse.statusText}`);
        }

        const searchData = await searchResponse.json();

        if (!searchData.success) {
          throw new Error(searchData.error || "Search failed");
        }

        const ftsResults = searchData.data || [];
        const totalCount = searchData.total_count || searchData.count || 0;

        if (!isCurrent()) return;

        // If no results, return early
        if (ftsResults.length === 0) {
          setResults([]);
          setTotalCount(totalCount);
          setCacheStats({ cached: 0, fetched: 0 });
          setIsLoading(false);
          return;
        }

        // PROGRESSIVE ENHANCEMENT: Render FTS results immediately (9 fields)
        // This allows users to see and interact with results while we fetch full details
        const ftsResultsWithRelevance = ftsResults.map((r: any) => ({
          ...r,
          _relevance: r.relevance,
          _isPartial: true, // Mark as partial data
          _isLoading: true, // Mark as loading
        }));
        setResults(ftsResultsWithRelevance);
        setTotalCount(totalCount);

        // Step 2: Fetch full rows by ID with caching (in background)
        const ids = ftsResults.map((r: any) => r.id);
        const fullRowResponse = await fetch(`/api/entities/${entityType}/by-ids`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            ids,
            ttl: cacheTtl,
          }),
          signal: controller.signal,
        });

        if (!fullRowResponse.ok) {
          throw new Error(`Failed to fetch full rows: ${fullRowResponse.statusText}`);
        }

        const fullRowData = await fullRowResponse.json();

        if (!fullRowData.success) {
          throw new Error(fullRowData.error || "Failed to fetch full rows");
        }

        // Step 3: Merge FTS relevance scores with full row data
        const fullRows = fullRowData.data || [];
        const mergedResults = ftsResults.map((ftsResult: any, index: number) => {
          const fullRow = fullRows.find((row: any) => row.id === ftsResult.id);
          // by-ids drops any id it cannot read (RLS, a delete between the two
          // calls), so it legitimately returns FEWER rows than were asked for.
          // Spreading the missing row would leave `{}` — a row with no id and
          // no name, which the table renders as a blank line or drops entirely.
          // Keep the step-1 fields instead; the row stays usable, just partial.
          if (!fullRow) {
            return ftsResultsWithRelevance[index];
          }
          return {
            ...fullRow,
            _relevance: ftsResult.relevance, // Preserve relevance score
            _isPartial: false, // Mark as complete data
            _isLoading: false, // Mark as no longer loading
          };
        });

        if (!isCurrent()) return;

        // Update results with full data
        setResults(mergedResults);
        setTotalCount(totalCount);
        setCacheStats({
          cached: fullRowData.cached || 0,
          fetched: fullRowData.fetched || 0,
        });
      } catch (err) {
        // Don't set error if request was aborted (user typed again) or if a
        // newer search has already taken over.
        if (err instanceof Error && err.name !== "AbortError" && isCurrent()) {
          const errorMessage = err instanceof Error ? err.message : "Unknown error";
          setError(errorMessage);
          // Deliberately KEEP the results already on screen. A failed search —
          // in production, a `search_contacts_hybrid` statement timeout — used
          // to blank the list, which reads to the user as results that appear
          // and then vanish. Stale-but-visible beats empty.
          setCacheStats(undefined);
        }
      } finally {
        // Only the newest run owns the spinner; an older run settling must not
        // signal "done" while the current search is still in flight.
        if (isCurrent()) {
          setIsLoading(false);
        }
      }
    },
    [entityType, limit, offset, cacheTtl, searchMode]
  );

  const search = useCallback(
    (query: string): void => {
      // Clear existing debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Set new debounce timer
      debounceTimerRef.current = setTimeout(() => {
        void performSearch(query);
      }, debounceMs);
    },
    [performSearch, debounceMs]
  );

  return {
    search,
    isLoading,
    error,
    results,
    totalCount,
    cacheStats,
  };
}
